import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button } from 'react-bootstrap';
import AdminDataTable from './AdminDataTable';
import Swal from 'sweetalert2';
import { FaChartLine, FaCubes, FaEdit, FaExchangeAlt, FaList, FaPlus, FaSearch, FaShieldAlt, FaTrash, FaUserClock } from 'react-icons/fa';
import AdminPageHeader from './AdminPageHeader';
import Navigation from './Navigation';
import { filterMenuModuleRecords, outletMenuModuleCatalog } from './outletMenuModuleRegistry';
import { isSuperAdminUser, readStoredAdminUser } from './adminAccessControl';
import useAuth from '../../hooks/useAuth';

const isActiveModuleStatus = value => {
    const normalized = String(value ?? '').trim().toLowerCase();
    return ['active', '1', 'true', 'yes', 'on'].includes(normalized);
};

const formatModuleStatusLabel = value => (
    isActiveModuleStatus(value) ? 'Active' : 'Inactive'
);

const isSuccessResponse = value => value === 0 || value === '0';

const normalizeRoutePath = value => {
    const normalized = String(value ?? '').trim();
    if (!normalized) {
        return '';
    }
    return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

function Modules() {
    const [modules, setModules] = useState([]);
    const [filteredModules, setFilteredModules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditSummary, setAuditSummary] = useState(null);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const { verifyAndRefreshToken, error } = useAuth();
    const currentUser = readStoredAdminUser();
    const isSuperAdmin = isSuperAdminUser(currentUser);
    const syncedRouteSet = useMemo(
        () => new Set(modules.map(module => normalizeRoutePath(module.Modulelink)).filter(Boolean)),
        [modules]
    );
    const syncedRegistryCount = useMemo(
        () => outletMenuModuleCatalog.filter(module => syncedRouteSet.has(module.path)).length,
        [syncedRouteSet]
    );
    const pendingSyncCount = Math.max(outletMenuModuleCatalog.length - syncedRegistryCount, 0);

    const fetchModules = useCallback(async (token) => {
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}module-master-list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ limit: 100, start: 0 }),
            });

            const data = await response.json();
            if (isSuccessResponse(data.error)) {
                const menuModules = filterMenuModuleRecords(data.results);
                setModules(menuModules);
                setFilteredModules(menuModules);
                return;
            }

            throw new Error(data.message || 'Failed to fetch modules');
        } catch (err) {
            console.error('Failed to fetch modules:', err);
            Swal.fire('Error', 'Failed to fetch modules', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAuditSummary = useCallback(async (token) => {
        setAuditLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}member-session-audit`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();
            if (isSuccessResponse(data.error)) {
                setAuditSummary(data.data || null);
                return;
            }

            throw new Error(data.message || 'Failed to fetch audit summary');
        } catch (err) {
            console.error('Failed to fetch member session audit:', err);
            setAuditSummary(null);
        } finally {
            setAuditLoading(false);
        }
    }, []);

    useEffect(() => {
        const initializeModules = async () => {
            if (!isSuperAdmin) {
                navigate('/dashboardadmin', { replace: true });
                return;
            }

            const token = localStorage.getItem('sessionToken');
            if (!token) {
                navigate('/loginadmin');
                return;
            }

            const isValid = await verifyAndRefreshToken(token);
            if (isValid) {
                const refreshedToken = localStorage.getItem('sessionToken');
                if (!refreshedToken) {
                    navigate('/loginadmin');
                    return;
                }

                fetchModules(refreshedToken);
                fetchAuditSummary(refreshedToken);
            }
        };

        initializeModules();
    }, [fetchAuditSummary, fetchModules, isSuperAdmin, navigate, verifyAndRefreshToken]);

    const handleSearch = useCallback((e) => {
        const value = e.target.value;
        setSearch(value);
        setFilteredModules(
            modules.filter(module =>
                module.Modulename.toLowerCase().includes(value.toLowerCase())
            )
        );
    }, [modules]);

    const handleDelete = useCallback(async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            const token = localStorage.getItem('sessionToken');
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}deletemodule`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ id }),
                });

                const data = await response.json();
                if (data.error === "0") {
                    Swal.fire('Deleted!', 'The module has been deleted.', 'success');
                    setModules(prevModules => prevModules.filter(module => module.ID !== id));
                    setFilteredModules(prevFiltered => prevFiltered.filter(module => module.ID !== id));
                } else {
                    Swal.fire('Error!', 'Failed to delete module.', 'error');
                }
            } catch (err) {
                console.error('Delete error:', err);
                Swal.fire('Error!', 'Failed to delete module.', 'error');
            }
        }
    }, []);

    const handleSyncModules = useCallback(async () => {
        if (!isSuperAdmin) {
            return;
        }

        const token = localStorage.getItem('sessionToken');
        if (!token) {
            navigate('/loginadmin');
            return;
        }

        const confirmation = await Swal.fire({
            title: 'Auto sync modules?',
            text: pendingSyncCount > 0
                ? `${pendingSyncCount} route module terdeteksi di browser dan akan dicek ulang dengan katalog server tanpa mengubah data yang sudah tersimpan.`
                : 'Browser tidak mendeteksi route baru, tapi server tetap akan mengecek katalog modul internal dan menambahkan yang belum ada.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sync sekarang',
            cancelButtonText: 'Batal',
        });

        if (!confirmation.isConfirmed) {
            return;
        }

        setSyncLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}module-master-sync`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({}),
            });

            const data = await response.json();
            if (!isSuccessResponse(data.error)) {
                throw new Error(data.message || 'Failed to sync modules');
            }

            await fetchModules(token);

            const inserted = Number(data?.data?.inserted || 0);
            const skipped = Number(data?.data?.skipped || 0);
            const invalid = Number(data?.data?.invalid || 0);

            Swal.fire(
                inserted > 0 ? 'Sync selesai' : 'Tidak ada perubahan',
                `${inserted} module ditambahkan, ${skipped} dilewati, ${invalid} invalid.`,
                inserted > 0 ? 'success' : 'info'
            );
        } catch (err) {
            console.error('Module sync error:', err);
            Swal.fire('Error', err.message || 'Failed to sync modules', 'error');
        } finally {
            setSyncLoading(false);
        }
    }, [fetchModules, isSuperAdmin, navigate, pendingSyncCount]);

    const columns = [
        { name: 'ID', selector: row => row.ID, sortable: true },
        {
            name: 'Module',
            sortable: true,
            grow: 1.6,
            cell: row => (
                <div className="table-identity">
                    <span className="table-identity__avatar">
                        {row.Modulename?.slice(0, 2).toUpperCase() || 'MD'}
                    </span>
                    <span className="table-identity__meta">
                        <strong>{row.Modulename}</strong>
                        <small>{row.Modulelink || 'No route configured'}</small>
                    </span>
                </div>
            ),
        },
        {
            name: 'Status',
            sortable: true,
            cell: row => (
                <span className={`status-pill ${isActiveModuleStatus(row.Modulestatus) ? '' : 'is-muted'}`}>
                    {formatModuleStatusLabel(row.Modulestatus)}
                </span>
            ),
        },
        {
            name: 'Assets',
            grow: 1.5,
            cell: row => (
                <span className="table-identity__meta">
                    <strong>{row.Filename}</strong>
                    <small>{row.Tablename} / {row.Databasename}</small>
                </span>
            ),
        },
        {
            name: 'Actions',
            cell: row => (
                <div className="table-action-group">
                    <Button
                        variant="outline-warning"
                        size="sm"
                        title="Edit module"
                        onClick={() => navigate(`/editmodule/${row.ID}`)}
                    >
                        <FaEdit /> <span className="d-none d-md-inline">Edit</span>
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        title="Delete module"
                        onClick={() => handleDelete(row.ID)}
                    >
                        <FaTrash /> <span className="d-none d-md-inline">Delete</span>
                    </Button>
                </div>
            ),
        },
    ];

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    const auditCards = [
        {
            key: 'legacy-current',
            icon: FaUserClock,
            label: 'Legacy Token Aktif',
            value: auditSummary?.legacy_current ?? 0,
            description: 'Member yang request terakhirnya masih memakai token lama.',
        },
        {
            key: 'migrated',
            icon: FaExchangeAlt,
            label: 'Migrated',
            value: auditSummary?.migrated ?? 0,
            description: 'Member legacy yang sudah otomatis dipindah ke refresh session.',
        },
        {
            key: 'pending',
            icon: FaShieldAlt,
            label: 'Pending Migration',
            value: auditSummary?.pending_migration ?? 0,
            description: 'Member legacy yang pernah terdeteksi tetapi belum tersentuh ulang.',
        },
        {
            key: 'sessions',
            icon: FaChartLine,
            label: 'Refresh Session Aktif',
            value: auditSummary?.active_refresh_sessions ?? 0,
            description: 'Total refresh session member yang masih aktif di Redis.',
        },
    ];

    return (
        <div>
            <Navigation />
            <Container className="admin-page-shell mt-4">
                <AdminPageHeader
                    icon={FaCubes}
                    eyebrow="System modules"
                    title="Module Management"
                    description="Kelola modul internal yang dipakai adminfitness untuk kebutuhan operasional harian."
                    breadcrumbs={[
                        { label: 'Dashboard', to: '/dashboardadmin' },
                        { label: 'Module List' },
                    ]}
                    actions={(
                        <div className="d-flex gap-2 flex-wrap justify-content-end">
                            <Button
                                variant="outline-secondary"
                                onClick={handleSyncModules}
                                disabled={syncLoading || loading}
                            >
                                <FaExchangeAlt /> {syncLoading ? 'Syncing...' : 'Auto Sync Modules'}
                            </Button>
                            <Button variant="primary" onClick={() => navigate('/addmodule')}>
                                <FaPlus /> Add Module
                            </Button>
                        </div>
                    )}
                    meta={(
                        <div className="d-flex flex-wrap gap-2">
                            <span className="soft-stat">
                                <FaList /> {filteredModules.length} modules
                            </span>
                            <span className="status-chip">
                                <FaExchangeAlt /> {pendingSyncCount} pending sync
                            </span>
                        </div>
                    )}
                />

                <div className="row g-3 mb-4">
                    {auditCards.map(card => {
                        const Icon = card.icon;
                        return (
                            <div key={card.key} className="col-12 col-md-6 col-xl-3">
                                <div className="section-card h-100">
                                    <div className="d-flex justify-content-between align-items-start gap-3">
                                        <div>
                                            <div className="text-uppercase text-muted small fw-semibold mb-2">
                                                {card.label}
                                            </div>
                                            <div className="display-6 fw-semibold mb-2">
                                                {auditLoading ? '...' : card.value}
                                            </div>
                                            <p className="text-muted mb-0">{card.description}</p>
                                        </div>
                                        <span className="soft-stat">
                                            <Icon />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="section-card table-card">
                    <div className="table-toolbar">
                        <div className="d-flex flex-wrap gap-2">
                            <span className="status-chip">
                                <FaList /> {filteredModules.length} modules
                            </span>
                            <span className="status-chip">
                                <FaExchangeAlt /> {syncedRegistryCount}/{outletMenuModuleCatalog.length} synced
                            </span>
                        </div>
                        <div className="control-with-icon search-control">
                            <span className="control-with-icon__icon">
                                <FaSearch />
                            </span>
                            <Form.Control
                                type="text"
                                placeholder="Search by module name"
                                value={search}
                                onChange={handleSearch}
                            />
                        </div>
                    </div>
                    <AdminDataTable
                        columns={columns}
                        data={filteredModules}
                        pagination
                        progressPending={loading}
                        highlightOnHover
                        pointerOnHover
                        striped
                        responsive
                        emptyTitle="No module data found"
                        emptyDescription="Tambahkan modul baru untuk mulai menyusun akses admin."
                    />
                </div>
            </Container>
        </div>
    );
}

export default Modules;
